// src/pages/orders/orderSource.js
//
// Resolves what the orders list's "Source" column should say for a row.
//
// WHY THIS EXISTS
// ---------------
// The Source cell used to render `order.channel`. That field is not a property of
// the purchase — it is a hardcoded literal stamped by whichever backend row
// producer emitted the row: 'SALEOR_US' for anything read out of the commerce
// Postgres database, 'SHAKERPRO' for the shakerpro PHP feed, 'US' for the legacy
// MySQL rows, 'COLLINS' for Collins. So it identifies the *pipeline*, not the
// *store*. Three genuinely different purchases — a Saleor mirror order, an
// engine-native barsys.com checkout, and a coaster.barsys.com sale — all arrive
// tagged 'SALEOR_US' and all rendered as one indistinguishable "barsys.com" badge.
//
// `order.sourceKey` is the real answer: the canonical origin of the purchase,
// taken verbatim from the commerce `orders.source` column. Expected values are
// domains — 'barsys.com', 'shakerpro.barsys.com', 'coaster.barsys.com',
// '360.barsys.com'.
//
// THE FIELD IS ABSENT, NOT A PLACEHOLDER, when the backend has no value for it.
// That is the whole reason this module is defensive. Every producer that is not
// commerce Postgres (legacy MySQL, Collins, the shakerpro feed, the ShopMy lens)
// can reasonably ship rows with no `sourceKey` at all, and this is the dashboard's
// primary screen — so a missing field must degrade to the exact pre-existing
// channel rendering, never to "undefined" and never to an empty cell.
//
// `order.ownership` ('mirror' | 'engine' | 'unknown') says who owns the order:
// 'mirror' means Saleor owns it, 'engine' means the commerce engine does. Saleor
// is being retired, so this is operationally useful but strictly secondary to the
// origin — it is surfaced in the badge tooltip rather than taking up cell width.

// Longest expected origin is 'shakerpro.barsys.com' (20 chars), which the cell
// already rendered before this change, so nothing real gets truncated. The cap is
// insurance: `orders.source` is a free-text column (staging rows carry values like
// 'engine-rehearsal'), and one unexpected long value must not blow out the column
// width on the dashboard's main screen. The untruncated value stays in the tooltip.
export const SOURCE_LABEL_MAX = 24;

// A usable sourceKey is a non-empty string. Anything else — undefined, null, '',
// whitespace, a number, an object — is treated as absent so the caller falls back.
export const normalizeSourceKey = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

export const normalizeOwnership = (value) => {
  if (typeof value !== "string") return null;
  const v = value.trim().toLowerCase();
  return v === "mirror" || v === "engine" ? v : null;
};

// The pre-sourceKey rendering, preserved verbatim so the fallback path is a
// behavioural no-op. Note two quirks that are intentionally kept:
//   * only the India *view* renames the store ("India store"); the UK view falls
//     through to "barsys.com" exactly as it did before.
//   * 'US' (legacy MySQL) and 'SALEOR_US' (commerce Postgres) both fall through.
export const channelFallbackLabel = (rowChannel, viewChannel) => {
  if (rowChannel === "SHAKERPRO") return "shakerpro.barsys.com";
  if (rowChannel === "COLLINS") return "ai.barsys.com";
  if (rowChannel === "SHOPMY") return "ShopMy";
  if (viewChannel === "India") return "India store";
  return "barsys.com";
};

export const truncateSourceLabel = (label) =>
  label.length > SOURCE_LABEL_MAX ? `${label.slice(0, SOURCE_LABEL_MAX - 1)}…` : label;

// Returns { label, title, accent, fromSourceKey } — everything the cell needs and
// nothing it doesn't. `accent` drives the theme-coloured badge variant.
export const resolveOrderSource = (order, viewChannel) => {
  const row = order && typeof order === "object" ? order : {};
  const sourceKey = normalizeSourceKey(row.sourceKey);
  const ownership = normalizeOwnership(row.ownership);

  // The origin is already a domain, so it is rendered as-is — that is what was
  // asked for, and it is what makes coaster.barsys.com visibly distinct from
  // barsys.com. Accent every origin that is not the main storefront, which
  // generalises the rule the channel rendering already applied to SHAKERPRO.
  const fromSourceKey = sourceKey !== null;
  const label = fromSourceKey
    ? sourceKey
    : channelFallbackLabel(row.channel, viewChannel);
  const accent = fromSourceKey ? sourceKey !== "barsys.com" : row.channel === "SHAKERPRO";

  const titleParts = [];
  if (fromSourceKey) titleParts.push(`Ordered on ${label}`);
  if (ownership === "mirror") titleParts.push("Saleor-owned (mirrored order)");
  else if (ownership === "engine") titleParts.push("Commerce-engine order");

  return {
    label: truncateSourceLabel(label),
    // undefined rather than "" so React omits the attribute entirely when there
    // is nothing useful to say.
    title: titleParts.length > 0 ? titleParts.join(" · ") : undefined,
    accent,
    fromSourceKey,
  };
};
